<script setup>
import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import CsatMetricCard from './CsatMetricCard.vue';
import CsatRatingDistribution from './CsatRatingDistribution.vue';
import CsatDynamicsChart from './CsatDynamicsChart.vue';
import { useSummaryReportArchive } from '../composables/useSummaryReportArchive';
import { resolveMetricKeyFromCardKey } from '../helpers/summaryReportArchiveHelper';
import { formatPeriodRangeLabel } from '../helpers/dynamicsGranularityHelper';

const props = defineProps({
  displayType: { type: String, default: 'emoji' },
  from: { type: Number, default: 0 },
  to: { type: Number, default: 0 },
  entityFilters: { type: Object, default: () => ({}) },
});

const { openArchive } = useSummaryReportArchive();

const metrics = useMapGetter('csat/getMetrics');
const comparisons = useMapGetter('csat/getComparisons');
const ratingPercentage = useMapGetter('csat/getRatingPercentage');
const ratingCount = useMapGetter('csat/getRatingCount');
const satisfactionScore = useMapGetter('csat/getSatisfactionScore');
const responseRate = useMapGetter('csat/getResponseRate');
const averageRating = useMapGetter('csat/getAverageRating');
const negativeRate = useMapGetter('csat/getNegativeRate');
const dynamics = useMapGetter('csat/getDynamics');
const uiFlags = useMapGetter('csat/getUIFlags');

const isLoading = computed(() => uiFlags.value.isFetchingMetrics);
const isDynamicsLoading = computed(() => uiFlags.value.isFetchingDynamics);

const formatCount = value =>
  value || value === 0 ? Number(value).toLocaleString() : '0';

const formatPercent = value =>
  value === null || value === undefined || value === '' ? '—' : `${value}%`;

const formatAverage = value =>
  value === null || value === undefined || value === ''
    ? '—'
    : Number(value).toFixed(1);

const formatPeriodLabel = (since, until) => {
  const label = formatPeriodRangeLabel(since, until);
  return label ? `${label} UTC` : '';
};

const primaryPeriodLabel = computed(() =>
  formatPeriodLabel(props.from, props.to)
);

const archiveContext = computed(() => ({
  from: props.from,
  to: props.to,
  entityFilters: props.entityFilters,
  reportType: 'csat',
}));

const buildComparisons = (key, formatter) =>
  (comparisons.value || []).map(slice => ({
    value: formatter(slice[key]),
    periodLabel: formatPeriodLabel(slice.since, slice.until),
  }));

const metricCards = computed(() => [
  {
    key: 'surveys-sent',
    label: 'CSAT_REPORTS.METRIC.SURVEYS_SENT.LABEL',
    tooltip: 'CSAT_REPORTS.METRIC.SURVEYS_SENT.TOOLTIP',
    value: formatCount(metrics.value.totalSentMessagesCount),
    raw: metrics.value.totalSentMessagesCount,
    comparisons: buildComparisons('total_sent_messages_count', formatCount),
  },
  {
    key: 'total-responses',
    label: 'CSAT_REPORTS.METRIC.TOTAL_RESPONSES.LABEL',
    tooltip: 'CSAT_REPORTS.METRIC.TOTAL_RESPONSES.TOOLTIP',
    value: formatCount(metrics.value.totalResponseCount),
    raw: metrics.value.totalResponseCount,
    comparisons: buildComparisons('total_count', formatCount),
  },
  {
    key: 'response-rate',
    label: 'CSAT_REPORTS.METRIC.RESPONSE_RATE.LABEL',
    tooltip: 'CSAT_REPORTS.METRIC.RESPONSE_RATE.TOOLTIP',
    value: formatPercent(responseRate.value),
    raw: metrics.value.totalResponseCount,
    comparisons: buildComparisons('response_rate', formatPercent),
  },
  {
    key: 'average-rating',
    label: 'CSAT_REPORTS.METRIC.AVERAGE_RATING.LABEL',
    tooltip: 'CSAT_REPORTS.METRIC.AVERAGE_RATING.TOOLTIP',
    value: formatAverage(averageRating.value),
    raw: metrics.value.totalResponseCount,
    comparisons: buildComparisons('average_rating', formatAverage),
  },
  {
    key: 'satisfaction-score',
    label: 'CSAT_REPORTS.METRIC.SATISFACTION_SCORE.LABEL',
    tooltip: 'CSAT_REPORTS.METRIC.SATISFACTION_SCORE.TOOLTIP',
    value: formatPercent(satisfactionScore.value),
    raw: metrics.value.totalResponseCount,
    comparisons: buildComparisons('satisfaction_rate', formatPercent),
  },
  {
    key: 'negative-rate',
    label: 'CSAT_REPORTS.METRIC.NEGATIVE_RATE.LABEL',
    tooltip: 'CSAT_REPORTS.METRIC.NEGATIVE_RATE.TOOLTIP',
    value: formatPercent(negativeRate.value),
    raw: metrics.value.totalResponseCount,
    comparisons: buildComparisons('negative_rate', formatPercent),
  },
]);

const onCardClick = card => {
  const metricKey = resolveMetricKeyFromCardKey('csat', card.key);
  if (!metricKey || !(card.raw > 0)) return;
  openArchive({
    metricKey,
    reportType: 'csat',
    context: archiveContext.value,
  });
};

const onRatingClick = rating => {
  if (!(ratingCount.value?.[rating] > 0)) return;
  openArchive({
    metricKey: 'csatResponses',
    reportType: 'csat',
    context: archiveContext.value,
    ratings: [rating],
    csatResponded: true,
  });
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <div
      class="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-6 w-full shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 px-6 py-5"
    >
      <CsatMetricCard
        v-for="metric in metricCards"
        :key="metric.key"
        :label="$t(metric.label)"
        :tooltip="$t(metric.tooltip)"
        :value="metric.value"
        :comparisons="metric.comparisons"
        :period-label="primaryPeriodLabel"
        :is-loading="isLoading"
        :linkable="metric.raw > 0"
        @click="onCardClick(metric)"
      />
    </div>

    <div class="grid gap-4 lg:grid-cols-2">
      <CsatRatingDistribution
        :rating-percentage="ratingPercentage"
        :rating-count="ratingCount"
        :total-response-count="metrics.totalResponseCount"
        :is-loading="isLoading"
        :display-type="props.displayType"
        @rating-click="onRatingClick"
      />
      <CsatDynamicsChart :rows="dynamics" :is-loading="isDynamicsLoading" />
    </div>
  </div>
</template>
